import type { BadgeProps, TableProps } from '@leafwell/components';
import { LocationItem } from 'lib/queries/locations';

import React, { memo, useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  Collapsible,
  Badge,
  Paragraph,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';

export type LocationHoursDay = {
  close?: number;
  open?: number;
};

export type LocationHoursWeek = Record<number, LocationHoursDay>;

const DaySchedule: React.FC<{
  className?: string;
  schedule: Omit<LocationHoursDay, 'day'>;
  isHeader?: boolean;
  isStatic?: boolean;
}> = memo(
  ({ className = '', schedule, isHeader = false, isStatic = false }) => {
    const { locale } = useRouter();

    const getFormattedTime = useCallback(
      (timestamp: number, isStatic: boolean) =>
        isStatic
          ? new Date(timestamp).toLocaleTimeString(locale, {
              timeZone: 'UTC', //to avoid timezone converstion when the partners info is handled by the file static-single-partner.ts
              hour: '2-digit',
              minute: '2-digit',
            })
          : new Date(timestamp).toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
            }),
      [locale],
    );

    const isClosed = schedule?.open === schedule?.close;
    return isHeader && isClosed ? null : (
      <Paragraph {...{ className, tagName: 'span', variant: 'medium' }}>
        {!isClosed ? getFormattedTime(schedule.open, isStatic) : 'Closed'}
        {!isClosed && <span> - </span>}
        {!isClosed ? getFormattedTime(schedule.close, isStatic) : null}
      </Paragraph>
    );
  },
);

const StatusBadge: React.FC<{
  className?: string;
  today: React.MutableRefObject<Date>;
  todaySchedule: LocationHoursDay;
}> = ({ className = '', today, todaySchedule }) => {
  const { translations } = useWordPressContext();
  // @ts-ignore
  const status: { label: string; variant: BadgeProps['variant'] } =
    useMemo(() => {
      const day = today?.current;
      const closedStatus = { label: 'Closed', variant: 'default' };

      if (!day || !todaySchedule) {
        return closedStatus;
      }

      const time = day.getTime();
      const closesSoon = todaySchedule.close - 3600 * 1000; // 1h in ms
      const isAfterOpening = todaySchedule.open < time;

      if (isAfterOpening && closesSoon > time) {
        return { label: 'Open', variant: 'primary' };
      }

      if (isAfterOpening && todaySchedule.close > time) {
        return { label: 'Closes soon', variant: 'warn' };
      }

      return closedStatus;
    }, [today, todaySchedule]);

  return (
    <Badge
      {...{
        className,
        label: translations[status.label],
        size: 'small',
        variant: status.variant,
      }}
    />
  );
};

const WeekSchedules: React.FC<
  {
    schedules: LocationHoursWeek;
    isStatic?: boolean;
  } & TableProps
> = memo(({ schedules, isStatic = false, ...props }) => {
  const { locale } = useRouter();

  const getLocaleWeekday = useCallback(
    timestamp =>
      new Date(timestamp).toLocaleString(locale, {
        weekday: 'long',
      }),
    [locale],
  );

  return (
    <Table {...props}>
      <TableBody>
        {/* @ts-ignore */}
        {schedules.map((schedule, key) => (
          <TableRow {...{ key }}>
            <TableCell {...{ className: 'pr-2', header: true }}>
              {getLocaleWeekday(schedule.open)}
            </TableCell>
            <TableCell>
              <DaySchedule {...{ schedule, isStatic }} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
});

const LocationOpeningHours: React.FC<
  Pick<LocationItem, 'openingHours'> & {
    className?: string;
    showHours?: boolean;
    isStatic?: boolean;
  }
> = ({ className, openingHours, showHours = true, isStatic = false }) => {
  const { translations } = useWordPressContext();
  const today = useRef(new Date());

  const todaySchedule = useMemo(() => {
    const day = today?.current;

    if (!day || !openingHours) {
      return null;
    }

    return openingHours?.[day.getDay()];
  }, [openingHours, today]);

  return showHours ? (
    // @ts-ignore
    <Collapsible
      {...{
        buttonAlign: 'start',
        title: (
          <div {...{ className: 'flex gap-x-3 items-center' }}>
            <StatusBadge
              {...{
                className,
                today,
                todaySchedule,
              }}
            />
            {todaySchedule && (
              <DaySchedule
                {...{ schedule: todaySchedule, isHeader: true, isStatic }}
              />
            )}
            <span className="sr-only">
              {translations['Toggle weekly opening hours']}
            </span>
          </div>
        ),
        variant: 'primary',
      }}
    >
      <WeekSchedules {...{ schedules: openingHours, isStatic }} />
    </Collapsible>
  ) : (
    <StatusBadge
      {...{
        className,
        today,
        todaySchedule,
      }}
    />
  );
};

export default LocationOpeningHours;
